import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useRef, useState } from "react";
import XookeeperService from "../Services/XookeeperService";
import { errorNotification } from "../../Generic/Notifications";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import EmptyState from "../../Generic/EmptyState";
import styled from "styled-components";
import Loader from "../../Generic/Loader";
import LandingConnector from "../../Landing/Connector/LandingConnector";
import { useNavigate } from "react-router-dom";

const Page = styled.div`
   height:100%;
   width:100%;
   display:flex;
   flex-direction:column;
   padding:0 1.5rem 1.5rem 1.5rem;
   overflow-Y:auto
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
`;

const CenteredContainer = styled.div`
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
`;

const COMPONENTS = {
	TASKS: "TASKS",
};

const COMPONENT_STATES = {
	LOADING: "LOADING",
	ERROR: "ERROR",
	OK: "OK",
	HIDDEN: "HIDDEN",
};

const INITIAL_COMPONENT_STATES = {
	[COMPONENTS.TASKS]: COMPONENT_STATES.LOADING,
};

const XookeeperPastTasks = (props) => {
	const [componentStates, setComponentStates] = useState(INITIAL_COMPONENT_STATES);
	const [tasks, setTasks] = useState([]);
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [lastElement, setLastElement] = useState();
	const hasMore = useRef(true);

	function updateComponentState(component, state) {
		if (!(component in COMPONENTS) || !(state in COMPONENT_STATES)) return;
		setComponentStates((prev) => ({ ...prev, [component]: state }));
	}

	async function getPastTasks() {

		const payload = {
			statuses: [4, 5, 6, 7],
			kioskId: props.machineID,
			page,
			size: 10,
		}

		const { data, error } = await XookeeperService.getPastTasks(payload);
		if (data) {
			const { respWfList, count } = data;
			setTasks(prev => 
				prev.length ? [...prev, ...respWfList] : respWfList
			);

			if(count <= tasks.length) {
				hasMore.current = false;
			}

			updateComponentState(COMPONENTS.TASKS, COMPONENT_STATES.OK);
		}
		if (error) {
			errorNotification(error);
			updateComponentState(COMPONENTS.TASKS, COMPONENT_STATES.ERROR);
		}
	}

	function renderTasks(data, componentState) {
		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				return (
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				);

			case COMPONENT_STATES.HIDDEN:
				return;

			case COMPONENT_STATES.ERROR:
				return <EmptyState retry={getPastTasks} />;

			case COMPONENT_STATES.OK:
				if (tasks.length) {
					let tasksRenderData = data.map((task, index, array) => {
						const { workflowId, startDate, endAt, taskType, assignee, status } = task;
						return (
							<Trow ref={(node) => (index === array.length - 1 ? setLastElement(node) : null)} >
								<Tcol borderRadius="10px 0 0 10px">{workflowId}</Tcol>
								<Tcol>{startDate}</Tcol>
								<Tcol>{endAt}</Tcol>
								<Tcol>{taskType}</Tcol>
								<Tcol>{assignee}</Tcol>
								<Tcol borderRadius="0 10px 10px 0">{status}</Tcol>
							</Trow>
						);
					});
					return (
						<Table>
							<Trow head>
								<Thead>Task ID</Thead>
								<Thead>Start Time</Thead>
								<Thead>End Time</Thead>
								<Thead>Task Type</Thead>
								<Thead>Assigned to</Thead>
								<Thead>Status</Thead>
							</Trow>
							<Tbody>{tasksRenderData}</Tbody>
						</Table>
					);
				} else {
					return <EmptyState />;
				}

			default:
				return;
		}
	}

	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if (hasMore.current) {
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Fires everytime page changes
		getPastTasks(true);
	}, [page]);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	return (
		<Page>
			<Div margin="1.5rem 0 0 0" width="100%" gap="1rem" alignItem="center">
				<span onClick={() => navigate(`/${props.machineID}/xookeeper/tasks/planned`)} className="material-symbols-outlined" style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<Header>Past Tasks</Header>
			</Div>
			{renderTasks(tasks, componentStates[COMPONENTS.TASKS])}
		</Page>
	);
};

export default LandingConnector(XookeeperPastTasks);
